import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useModal } from '@hooks/useModal';
import { Sizes, Spacing, Typography } from 'chatie-design-system';
import { Icon, QuestionCircle } from 'chatie-design-system/icon';

import Button from '@components/common/Button';
import StatInfoModal from '@components/modal/StatInfoModal';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IVoucher } from '@model';
import { getJosa } from '@utils/misc';
import { useWebView } from '@utils/webview';

const myBalloonCSS = (theme: Theme) => css`
  position: relative;
  padding: ${Spacing.xlarge}px ${Spacing.medium}px;
  border-bottom: 1px solid ${theme.var.gray200};
  background: ${theme.var.white};
  z-index: 4;

  .main500 {
    color: ${theme.colors.main500};
  }
  .matric {
    color: ${theme.var.gray500};
    align-self: center;
    ${Typography.body12};
  }
  .h1 {
    ${Typography.h1};
  }
  .body-14 {
    ${Typography.body14};
  }
  .h5 {
    ${Typography.h5};
    font-weight: 600;
    line-height: 100%;
  }
  .white {
    color: ${theme.true.white};
    z-index: 999;
  }

  .balloon-count {
    display: flex;
    align-items: baseline;
    height: 24px;
    gap: ${Spacing.xxsmall}px;
  }

  .balloon-conatiner {
    display: flex;
    flex-direction: row;
  }

  .current-balloon-container {
    display: flex;
    flex-direction: column;
    gap: ${Spacing.xsmall}px;
  }
  .current-balloon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .icon-btn {
    display: flex;
    flext-direction: row;
    align-items: start;
    height: 100%;
    padding-top: 1px;
    i {
      display: flex;
      align-items: start;
    }
  }
  .icon-container {
    display: flex;
    gap: ${Spacing.xxsmall}px;
  }
  .voucher-info {
    padding: ${Spacing.xsmall}px 10px;
    margin-top: ${Spacing.xsmall}px;
    background: #f2f8f9;
    align-items: flex-start;
    gap: 10px;

    p {
      color: ${theme.colors.main500};
      font-family: 'Apple SD Gothic Neo';
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
    }
  }
`;

const historyButtonCSS = (theme: Theme) => css`
  position: absolute;
  top: 33px;
  right: 16px;
  width: 85px;
  height: 28px;
  color: ${theme.var.gray900};
  border-radius: 5px;
  border: 1px solid ${theme.var.gray200};
  box-shadow: none !important;
  ${Typography.body12};
`;

type ItemStatContainerProps = {
  balloon?: number;
  voucher?: IVoucher;
  messageToken?: number;
};

const BalloonStatContainer = ({
  balloon,
  voucher,
  messageToken,
}: ItemStatContainerProps) => {
  const { t } = useTranslation();
  const { platform, goodsName } = useGlobalContext();
  const { web } = useWebView();
  const router = useRouter();

  const [
    isStatInfoModalOpen,
    { open: openStatInfoModal, close: closeStatInfoModal },
  ] = useModal('stat-info-modal');

  const handleClickHistory = () => {
    const url = '/me/balloon-histories';
    if (platform === Platform.web) {
      return router.push(url);
    } else {
      web.openPage(url);
    }
  };

  const handleClickInfoIcon = () => {
    openStatInfoModal();
  };

  const theme = useTheme();

  return (
    <>
      <div css={myBalloonCSS}>
        <div className="balloon-conatiner">
          <div className="current-balloon-container">
            <div className="current-balloon-title">
              <p className="body-14">{t('현재 보유 아이템')}</p>
            </div>
            <div className="current-balloon">
              <div className="balloon-count">
                <span className="main500 h1">
                  {t('formatNumber', { val: balloon ?? 0 })}
                </span>
                <span className="matric">{t(goodsName)}</span>
                <span className="matric">{t('/')}</span>
                <span className="main500 h1">
                  {t('formatNumber', { val: messageToken ?? 0 })}
                </span>
                <span className="matric icon-container">
                  {t('대화권')}

                  <button className="icon-btn" onClick={handleClickInfoIcon}>
                    <Icon
                      icon={QuestionCircle}
                      size={Sizes.small}
                      color={theme.var.gray400}
                    />
                  </button>
                </span>
              </div>
            </div>
          </div>
          <Button
            css={historyButtonCSS}
            text={`${goodsName} 사용 내역`}
            onClick={handleClickHistory}
            backgroundColor="white"
            type={'border'}
            color={'black'}
          />
        </div>
        {!!voucher && (
          <div className="voucher-info">
            <p className="multiline">
              {t(
                `${getJosa(
                  voucher?.name,
                  '가'
                )} 적용됩니다.\n결제 충전한 ${goodsName}의 ${
                  voucher?.effectValue
                }%가 추가 지급됩니다.`
              )}
            </p>
          </div>
        )}
      </div>
      <StatInfoModal
        open={isStatInfoModalOpen}
        onClose={closeStatInfoModal}
        name={'stat-info-modal'}
      />
    </>
  );
};

export default BalloonStatContainer;
